import { useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';

/**
 * A floating "← Home" pill for interior marketing pages, mounted once in
 * PublicShell so it appears site-wide (How it works, Education, Marketplace,
 * Blog, Students, etc.). It's the always-available return path for ANONYMOUS
 * visitors — signed-in users get the AppShell sidebar instead, so the pill
 * lives only in PublicShell.
 *
 * Scroll detection — the important part. This app does NOT scroll on the
 * window: `window.scrollY` stays 0 (the real scroll happens on a container
 * whose identity varies), and the marketing nav isn't fixed. So we do NOT read
 * any `scrollTop`. Instead we render a zero-height SENTINEL in the normal
 * content flow (right below the nav) and, on every scroll (captured at the
 * window so it catches scrolls from any container — scroll doesn't bubble but
 * capture-phase fires for descendants), read the sentinel's
 * `getBoundingClientRect().top`. That value is viewport-relative and correct no
 * matter which element scrolls: it starts positive (just under the nav) and
 * goes negative as the page scrolls up. Once it passes above the viewport top
 * (nav scrolled off), we show the pill. This is the approach proven to work;
 * an earlier `body.scrollTop`-based version silently never fired.
 *
 * Clicking resets scroll to the top after the route swaps in, so you land at
 * the top of the homepage rather than mid-page.
 */
export function HomePill() {
  const [show, setShow] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Different pages scroll differently (some scroll the whole page, some
    // scroll an inner container). Rather than depend on one mechanism, OR
    // together every reliable signal so the pill shows whichever applies:
    //  1) the sentinel's viewport rect (works when the page itself scrolls),
    //  2) the scrollTop of the element that actually fired the scroll event
    //     (works when an inner container scrolls — read straight off e.target),
    //  3) the document/window offset (the plain case).
    const check = (e?: Event) => {
      const el = sentinelRef.current;
      const rectPast = el ? el.getBoundingClientRect().top <= -8 : false;

      let targetPast = false;
      const tgt = e?.target as HTMLElement | Document | null;
      if (tgt && tgt !== document && typeof (tgt as HTMLElement).scrollTop === 'number') {
        targetPast = (tgt as HTMLElement).scrollTop > 100;
      }

      const docTop =
        document.documentElement.scrollTop || document.body.scrollTop || window.scrollY || 0;

      // ~100px clears the (non-fixed) nav before the pill shows, so it appears
      // promptly on essentially every viewport as you scroll without ever
      // overlapping the nav logo at the very top.
      setShow(rectPast || targetPast || docTop > 100);
    };
    check();
    // capture:true catches scroll from whatever element actually scrolls
    // (scroll events fire in capture phase on ancestors even though they don't
    // bubble); passive for scroll performance.
    window.addEventListener('scroll', check, { capture: true, passive: true });
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', check);
    };
  }, []);

  return (
    <>
      {/* Zero-height, non-interactive sentinel in the normal content flow. Its
          viewport-relative position is what we measure (see note above). */}
      <div ref={sentinelRef} aria-hidden="true" style={{ height: 0, width: '100%' }} />
      {show && (
        <>
          <style>{`
            @keyframes homePillIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }
            /* Hover → brass fill (founder request). White text for contrast on
               the brass; border/shadow deepen to match. */
            .home-pill { transition: background 140ms ease, border-color 140ms ease, color 140ms ease, box-shadow 140ms ease; }
            .home-pill:hover {
              background: var(--brass, #b5853c) !important;
              border-color: var(--brass, #b5853c) !important;
              color: #fff !important;
              box-shadow: 0 8px 24px rgba(181,133,60,0.34) !important;
            }
          `}</style>
          <Link
            href="/"
            className="home-pill"
            aria-label="Back to home"
            onClick={() => {
              // The window isn't the scroller, so a route change alone keeps the
              // current scroll offset and would land you mid-homepage. Reset the
              // likely scroll containers to the top after the route swaps in.
              const toTop = () => {
                window.scrollTo(0, 0);
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
                const main = document.querySelector('main');
                if (main) main.scrollTop = 0;
              };
              requestAnimationFrame(() => requestAnimationFrame(toTop));
            }}
            style={{
              position: 'fixed',
              top: 18,
              left: 18,
              zIndex: 200,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '9px 15px 9px 13px',
              borderRadius: 999,
              background: 'var(--surface-card, #fbf8f0)',
              border: '1px solid var(--border-hairline, rgba(27,58,46,0.14))',
              boxShadow: '0 6px 20px rgba(15,23,20,0.14)',
              color: 'var(--heartwood, #1b3a2e)',
              fontFamily: 'var(--f-sans)',
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
              animation: 'homePillIn 200ms ease',
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 15, lineHeight: 1 }}>&larr;</span> Home
          </Link>
        </>
      )}
    </>
  );
}
